import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BackgroundVideo } from '../../components/BackgroundVideo';
import { BlurBox } from '../../components/BlurBox/BlurBox';
import { Button } from '../../components/Button';
import { Heading } from '../../components/Heading';
import { Icon } from '../../components/Icon';
import { SSOLoginButton } from '../../components/SSOLoginButton';
import { GTMBaseContentViewObject, PageTracker } from '../../hooks/GTMUtils';
import { useAppRoutes } from '../../hooks/useAppRoutes';
import { useAuth } from '../../hooks/useAuth';
import { useGTM } from '../../hooks/useGTM';
import { ReactComponent as InnovateMobile } from '../../images/innovate-mobile.svg';
import { ReactComponent as Innovate } from '../../images/innovate.svg';
import { useWindowSize } from '../../shared/useWindowSize';
import { TrackingSchema } from '../../types';

export const Intro: FC = () => {
  const { t } = useTranslation();
  const { briefingSelectionRoute } = useAppRoutes();
  const navigate = useNavigate();
  const [windowWidth] = useWindowSize();
  const { updateDataLayer } = useGTM();
  const [{ auth }] = useAuth();
  const { pathname } = useLocation();

  const isLanding = pathname === '/';

  const gtmTrackingData = {
    ...GTMBaseContentViewObject,
    data: {
      category: 'Home',
      type: isLanding ? 'Landing' : 'Overview',
      content: 'e-Lab Overview',
    },
  };

  return (
    <PageTracker gtmTrackingData={gtmTrackingData}>
      <BackgroundVideo name="intro" fullscreen />
      <div className="flex justify-center px-8 lg:px-0 lg:mb-12 mb-12 h-full items-center lg:max-w-5xl lg:m-auto">
        <div className="flex flex-col items-center">
          <div className="w-full max-w-[430px] md:max-w-none md:w-10/12">
            <div className="mt-24 text-white opacity-75 fill-current md:mt-32 mb-12 md:mb-24">
              {windowWidth >= 768 ? <Innovate /> : <InnovateMobile />}
            </div>
          </div>
          <div className="w-full md:w-10/12 z-40">
            <BlurBox>
              <div className="flex flex-col items-center">
                <Heading
                  text={t('components:intro:heading:main')}
                  level="h1"
                  align="center"
                  margin={6}
                />
                <p className="font-regular text-center">
                  {t('components:intro:content:main')}
                </p>
                <div className={'my-8 text-center'}>
                  {auth.uid ? (
                    <Button
                      label={t('linksOrButtons:navigation:start')}
                      onClick={() => navigate(briefingSelectionRoute)}
                    />
                  ) : (
                    <>
                      <SSOLoginButton />
                      <span className="block m-3 lg:inline-block">
                        {t('labels:or')}
                      </span>
                      <Button variant={'secondary'}>
                        <a
                          href={`${process.env.REACT_APP_CP_BASEURL}/customer/user/registration`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            updateDataLayer({
                              event: 'Content Click',
                              schema: TrackingSchema.content,
                              data: {
                                category: 'Customer Portal',
                                type: 'Customer Portal Redirect',
                                content: 'e-Lab Registration',
                              },
                            });
                          }}
                        >
                          {t('labels:requestAccess')}
                        </a>
                      </Button>
                    </>
                  )}
                </div>
                <Heading
                  text={t('components:intro:heading:about')}
                  level="h2"
                  align="center"
                  margin={4}
                />
                <ul>
                  {(
                    t('components:intro:content:benefits', {
                      returnObjects: true,
                    }) as string[]
                  ).map((item, index) => (
                    <li key={index} className={'flex items-center mb-4 font-regular'}>
                      <span
                        className={
                          'rounded-full bg-text1 text-white w-6 h-6 inline-block mr-4 p-1 flex-shrink-0'
                        }
                      >
                        <Icon name="Checkmark" />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </BlurBox>
          </div>
        </div>
      </div>
    </PageTracker>
  );
};
